// src/actions/adminProductActions.js

import api from "../utils/axiosInstance";
import * as A from "../constants/adminProductConstants";
import {
  USER_FACING_FALLBACK,
  getApiErrorMessage,
} from "../utils/errorMessages";
import { SHOP_KEYS } from "../constants/shopMeta";

const API_BASE = "/admin/products";

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const VALID_SHOP_KEYS = new Set(SHOP_KEYS);
const VALID_LIST_STATUS = new Set(["all", "published", "draft", "deleted"]);
const VALID_LIFECYCLE_STATUS = new Set(["published", "draft"]);

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

function normalizeShopKey(value = "") {
  const normalized = safeText(value);
  return VALID_SHOP_KEYS.has(normalized) ? normalized : "";
}

function normalizeListStatus(value = "", fallback = "all") {
  const normalized = normalizeLower(value);
  return VALID_LIST_STATUS.has(normalized) ? normalized : fallback;
}

function normalizeLifecycleStatus(value = "") {
  const normalized = normalizeLower(value);

  if (!VALID_LIFECYCLE_STATUS.has(normalized)) {
    throw buildClientErrorPayload({
      message: 'status must be "published" or "draft".',
      code: "INVALID_STATUS",
      status: 400,
    });
  }

  return normalized;
}

function normalizeModeValue(value = "", fallback = "") {
  return normalizeLower(value || fallback);
}

function toSafeStatus(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function getErrorMessage(error, fallback = USER_FACING_FALLBACK) {
  return getApiErrorMessage(error, { fallback });
}

function buildErrorPayload(error, fallback = USER_FACING_FALLBACK) {
  const data = error?.response?.data || {};

  return {
    message: String(getErrorMessage(error, fallback) || fallback),
    code: String(data?.code || error?.errorCode || ""),
    status: toSafeStatus(error?.response?.status || error?.statusCode || 0),
    requestId: String(
      data?.requestId ||
        error?.requestId ||
        error?.response?.headers?.["x-request-id"] ||
        "",
    ),
    issues: Array.isArray(data?.issues) ? data.issues : undefined,
  };
}

function buildClientErrorPayload({
  message,
  code = "BAD_REQUEST",
  status = 400,
  requestId = "",
}) {
  return {
    message: String(message || USER_FACING_FALLBACK),
    code: String(code || ""),
    status: toSafeStatus(status, 400),
    requestId: String(requestId || ""),
  };
}

function getAuthConfig(getState) {
  const state = getState?.() || {};

  const userInfo =
    state.userAuth?.userInfo ||
    state.userLogin?.userInfo ||
    state.userSignin?.userInfo ||
    state.auth?.userInfo ||
    null;

  return {
    headers: userInfo?.token
      ? {
          Authorization: `Bearer ${userInfo.token}`,
        }
      : {},
  };
}

function getJsonAuthConfig(getState) {
  const authConfig = getAuthConfig(getState);

  return {
    ...authConfig,
    headers: {
      "Content-Type": "application/json",
      ...(authConfig.headers || {}),
    },
  };
}

function cleanParams(params = {}) {
  const normalized = {
    ...params,
    shopKey: normalizeShopKey(params.shopKey),
    status: params.status ? normalizeListStatus(params.status) : undefined,
  };

  return Object.fromEntries(
    Object.entries(normalized).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    }),
  );
}

function encodePathSegment(value) {
  return encodeURIComponent(safeText(value));
}

function assertIdPresent(id) {
  const safe = safeText(id);

  if (!safe) {
    throw buildClientErrorPayload({
      message: "Product id is required.",
      code: "MISSING_ID",
      status: 400,
    });
  }

  return safe;
}

function unwrapProductResponse(data) {
  return data?.product || data;
}

function normalizeLifecyclePayload(data = {}, { id, status, mode } = {}) {
  return {
    ...data,
    productId: data?.productId || id || null,
    status: normalizeLower(data?.status || status),
    mode: normalizeModeValue(data?.mode || mode),
  };
}

/* -------------------------------------------------------------------------- */
/*                              Request runner                                */
/* -------------------------------------------------------------------------- */

const runRequest =
  ({
    requestType,
    successType,
    failType,
    method,
    urlBuilder,
    bodyBuilder,
    configBuilder,
    successPayloadBuilder,
    requestMetaBuilder,
    successMetaBuilder,
    fallbackErrorMessage = USER_FACING_FALLBACK,
  }) =>
  (...args) =>
  async (dispatch, getState) => {
    const requestMeta =
      typeof requestMetaBuilder === "function"
        ? requestMetaBuilder(...args)
        : undefined;

    try {
      dispatch({
        type: requestType,
        ...(requestMeta ? { meta: requestMeta } : {}),
      });

      const url = urlBuilder(...args);
      const body =
        typeof bodyBuilder === "function" ? bodyBuilder(...args) : undefined;
      const config =
        typeof configBuilder === "function"
          ? configBuilder(getState, ...args)
          : {};

      let response;

      switch (method) {
        case "get":
          response = await api.get(url, config);
          break;
        case "post":
          response = await api.post(url, body, config);
          break;
        case "put":
          response = await api.put(url, body, config);
          break;
        case "patch":
          response = await api.patch(url, body, config);
          break;
        case "delete":
          response = await api.delete(url, config);
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }

      const payload =
        typeof successPayloadBuilder === "function"
          ? successPayloadBuilder(response.data, ...args)
          : response.data;

      const successMeta =
        typeof successMetaBuilder === "function"
          ? successMetaBuilder(response.data, ...args)
          : undefined;

      const mergedMeta =
        requestMeta || successMeta
          ? { ...(requestMeta || {}), ...(successMeta || {}) }
          : undefined;

      dispatch({
        type: successType,
        payload,
        ...(mergedMeta ? { meta: mergedMeta } : {}),
      });

      return payload;
    } catch (error) {
      const payload =
        error?.status && error?.message
          ? error
          : buildErrorPayload(error, fallbackErrorMessage);

      dispatch({
        type: failType,
        payload,
        ...(requestMeta ? { meta: requestMeta } : {}),
      });

      throw payload;
    }
  };

/* -------------------------------------------------------------------------- */
/*                                    Admin                                   */
/* -------------------------------------------------------------------------- */

export const listAdminProducts = (params = {}) =>
  runRequest({
    requestType: A.ADMIN_PRODUCT_LIST_REQUEST,
    successType: A.ADMIN_PRODUCT_LIST_SUCCESS,
    failType: A.ADMIN_PRODUCT_LIST_FAIL,
    method: "get",
    urlBuilder: () => API_BASE,
    configBuilder: (getState) => ({
      ...getAuthConfig(getState),
      params: cleanParams(params),
    }),
    fallbackErrorMessage:
      "We could not load admin products right now. Please try again later.",
  })(params);

export const listAdminProductsByShop = (
  shopKey,
  params = { status: "all" },
) => {
  const safeShopKey = normalizeShopKey(shopKey);

  if (!safeShopKey) {
    throw buildClientErrorPayload({
      message: "Valid shopKey is required.",
      code: "INVALID_SHOP_KEY",
      status: 400,
    });
  }

  return listAdminProducts({
    ...params,
    shopKey: safeShopKey,
    status: normalizeListStatus(params.status || "all"),
  });
};

export const getAdminProductDetails = (id) => {
  const safeId = assertIdPresent(id);

  return runRequest({
    requestType: A.ADMIN_PRODUCT_DETAILS_REQUEST,
    successType: A.ADMIN_PRODUCT_DETAILS_SUCCESS,
    failType: A.ADMIN_PRODUCT_DETAILS_FAIL,
    method: "get",
    urlBuilder: (productId) => `${API_BASE}/${encodePathSegment(productId)}`,
    configBuilder: (getState) => getAuthConfig(getState),
    successPayloadBuilder: unwrapProductResponse,
    fallbackErrorMessage:
      "We could not load this product right now. Please try again later.",
  })(safeId);
};

export const createAdminProduct = (productPayload = {}) =>
  runRequest({
    requestType: A.ADMIN_PRODUCT_CREATE_REQUEST,
    successType: A.ADMIN_PRODUCT_CREATE_SUCCESS,
    failType: A.ADMIN_PRODUCT_CREATE_FAIL,
    method: "post",
    urlBuilder: () => API_BASE,
    bodyBuilder: (payload) => payload,
    configBuilder: (getState) => getJsonAuthConfig(getState),
    successPayloadBuilder: unwrapProductResponse,
    fallbackErrorMessage:
      "We could not create this product right now. Please try again later.",
  })(productPayload);

export const updateAdminProduct = (id, productPayload = {}) => {
  const safeId = assertIdPresent(id);

  return runRequest({
    requestType: A.ADMIN_PRODUCT_UPDATE_REQUEST,
    successType: A.ADMIN_PRODUCT_UPDATE_SUCCESS,
    failType: A.ADMIN_PRODUCT_UPDATE_FAIL,
    method: "put",
    urlBuilder: (productId) => `${API_BASE}/${encodePathSegment(productId)}`,
    bodyBuilder: (_productId, payload) => payload,
    configBuilder: (getState) => getJsonAuthConfig(getState),
    successPayloadBuilder: unwrapProductResponse,
    fallbackErrorMessage:
      "We could not update this product right now. Please try again later.",
  })(safeId, productPayload);
};

export const deleteAdminProduct = (id) => {
  const safeId = assertIdPresent(id);

  return runRequest({
    requestType: A.ADMIN_PRODUCT_DELETE_REQUEST,
    successType: A.ADMIN_PRODUCT_DELETE_SUCCESS,
    failType: A.ADMIN_PRODUCT_DELETE_FAIL,
    method: "delete",
    urlBuilder: (productId) => `${API_BASE}/${encodePathSegment(productId)}`,
    configBuilder: (getState) => getAuthConfig(getState),
    requestMetaBuilder: (productId) => ({
      id: productId,
      mode: "delete",
    }),
    successPayloadBuilder: (data, productId) => ({
      ...data,
      productId: data?.productId || productId,
      mode: "delete",
    }),
    successMetaBuilder: (_data, productId) => ({
      id: productId,
      mode: "delete",
    }),
    fallbackErrorMessage:
      "We could not delete this product right now. Please try again later.",
  })(safeId);
};

/* -------------------------------------------------------------------------- */
/*                                  Lifecycle                                 */
/* -------------------------------------------------------------------------- */

async function executeStatusRequest(dispatch, getState, options = {}) {
  const {
    id,
    status,
    mode = "status",
    legacyTypes = null,
    fallbackErrorMessage =
      "We could not update this product status right now. Please try again later.",
  } = options;

  const safeId = assertIdPresent(id);
  const normalizedStatus = normalizeLifecycleStatus(status);

  const meta = {
    id: safeId,
    status: normalizedStatus,
    mode: normalizeModeValue(mode, "status"),
  };

  dispatch({
    type: A.ADMIN_PRODUCT_SET_STATUS_REQUEST,
    meta,
  });

  if (legacyTypes?.request) {
    dispatch({
      type: legacyTypes.request,
      meta,
    });
  }

  try {
    const response = await api.patch(
      `${API_BASE}/${encodePathSegment(safeId)}/status`,
      { status: normalizedStatus },
      getJsonAuthConfig(getState),
    );

    const payload = normalizeLifecyclePayload(response.data, meta);

    dispatch({
      type: A.ADMIN_PRODUCT_SET_STATUS_SUCCESS,
      payload,
      meta,
    });

    if (legacyTypes?.success) {
      dispatch({
        type: legacyTypes.success,
        payload,
        meta,
      });
    }

    return payload;
  } catch (error) {
    const payload =
      error?.status && error?.message
        ? error
        : buildErrorPayload(error, fallbackErrorMessage);

    dispatch({
      type: A.ADMIN_PRODUCT_SET_STATUS_FAIL,
      payload,
      meta,
    });

    if (legacyTypes?.fail) {
      dispatch({
        type: legacyTypes.fail,
        payload,
        meta,
      });
    }

    throw payload;
  }
}

export const setAdminProductStatus = (id, status, options = {}) => {
  const safeId = assertIdPresent(id);

  return async (dispatch, getState) =>
    executeStatusRequest(dispatch, getState, {
      id: safeId,
      status,
      mode: options?.mode || "status",
      fallbackErrorMessage:
        "We could not update this product status right now. Please try again later.",
    });
};

export const publishAdminProduct = (id) =>
  setAdminProductStatus(id, "published", { mode: "publish" });

export const moveAdminProductToDraft = (id) =>
  setAdminProductStatus(id, "draft", { mode: "draft" });

export const restoreAdminProduct = (id, status = "draft") => {
  const safeId = assertIdPresent(id);

  return async (dispatch, getState) =>
    executeStatusRequest(dispatch, getState, {
      id: safeId,
      status,
      mode: "restore",
      legacyTypes: {
        request: A.ADMIN_PRODUCT_RESTORE_REQUEST,
        success: A.ADMIN_PRODUCT_RESTORE_SUCCESS,
        fail: A.ADMIN_PRODUCT_RESTORE_FAIL,
      },
      fallbackErrorMessage: "Failed to restore product.",
    });
};

export const unpublishAdminProduct = (id) => {
  const safeId = assertIdPresent(id);

  return async (dispatch, getState) =>
    executeStatusRequest(dispatch, getState, {
      id: safeId,
      status: "draft",
      mode: "unpublish",
      legacyTypes: {
        request: A.ADMIN_PRODUCT_UNPUBLISH_REQUEST,
        success: A.ADMIN_PRODUCT_UNPUBLISH_SUCCESS,
        fail: A.ADMIN_PRODUCT_UNPUBLISH_FAIL,
      },
      fallbackErrorMessage: "Failed to move product to draft.",
    });
};

/* -------------------------------------------------------------------------- */
/*                         Backwards-compatible aliases                        */
/* -------------------------------------------------------------------------- */

export const adminListProducts = listAdminProducts;
export const adminListProductsByShop = listAdminProductsByShop;
export const adminGetProductDetails = getAdminProductDetails;
export const adminCreateProduct = createAdminProduct;
export const adminUpdateProduct = updateAdminProduct;
export const adminDeleteProduct = deleteAdminProduct;
export const adminSetProductStatus = setAdminProductStatus;
export const adminRestoreProduct = restoreAdminProduct;
export const adminUnpublishProduct = unpublishAdminProduct;

/* -------------------------------------------------------------------------- */
/*                                    Reset                                   */
/* -------------------------------------------------------------------------- */

export const resetAdminProductList = () => ({
  type: A.ADMIN_PRODUCT_LIST_RESET,
});

export const resetAdminProductDetails = () => ({
  type: A.ADMIN_PRODUCT_DETAILS_RESET,
});

export const resetAdminProductCreate = () => ({
  type: A.ADMIN_PRODUCT_CREATE_RESET,
});

export const resetAdminProductUpdate = () => ({
  type: A.ADMIN_PRODUCT_UPDATE_RESET,
});

export const resetAdminProductDelete = () => ({
  type: A.ADMIN_PRODUCT_DELETE_RESET,
});

export const resetAdminProductSetStatus = () => ({
  type: A.ADMIN_PRODUCT_SET_STATUS_RESET,
});

export const resetAdminProductUnpublish = () => ({
  type: A.ADMIN_PRODUCT_UNPUBLISH_RESET,
});

export const resetAdminProductRestore = () => ({
  type: A.ADMIN_PRODUCT_RESTORE_RESET,
});
